<template>
  <tr
    :class="{
      'blank-row': isBlank,
      'drag-over': isDragOver && !isBlank,
      'drag-over-bottom': isDragOver && isBlank,
      'comment-row': isComment,
    }"
    @dragover.prevent="$emit('dragOver')"
    @drop.prevent="$emit('drop')">
    <td class="table-select type-cell">
      <select v-model="item.LINETYPE" @change="handleTypeChange">
        <option value="part">Part</option>
        <option value="nonstock">Non-stock</option>
        <option value="comment">Comment</option>
      </select>
    </td>

    <template v-if="isComment">
      <td class="table-input comment-cell" colspan="6">
        <textarea v-model="item.DESCRIPTION" rows="2" placeholder="Comment" @input="emitChanged" @focus="$event.target.select()"></textarea>
      </td>
    </template>

    <template v-else>
      <td class="table-input item-cell">
        <QuoteProductSearch
          v-if="item.LINETYPE !== 'nonstock'"
          :modelValue="item.DESCRIPTION || ''"
          :products="products"
          @update:modelValue="handleProductSearchInput"
          @selectProduct="selectProduct" />
        <input v-else v-model="item.DESCRIPTION" placeholder="Description" @input="emitChanged" @focus="$event.target.select()" />
      </td>

      <td class="table-input qty-cell">
        <QuoteLineNumberInput v-model="item.QTY" placeholder="Qty" @change="emitChanged" />
      </td>

      <td class="table-select unit-cell">
        <select v-model="item.UNIT" @change="handleUnitChange">
          <option v-for="option in unitOptions" :key="option" :value="option">{{ option }}</option>
        </select>
      </td>

      <td :class="['price-cell', item.LINETYPE === 'part' ? 'amount-cell' : 'table-input']">
        <template v-if="item.LINETYPE === 'part'">{{ formattedPrice }}</template>
        <QuoteLineNumberInput v-else v-model="item.UNITPRICE" placeholder="Price" @change="emitChanged" />
      </td>

      <td class="table-input discount-cell">
        <QuoteLineNumberInput v-model="item.DISCOUNTPCT" placeholder="Discount" tabindex="-1" @change="emitChanged" />
      </td>

      <td class="amount-cell">{{ formattedAmount }}</td>
    </template>

    <td class="actions-cell">
      <div v-if="!isBlank" class="line-actions">
        <button type="button" tabindex="-1" class="delete-btn" title="Delete line" @click="$emit('delete')">
          <img id="noHover" src="../../assets/trash.svg" alt="" />
          <img id="hover" src="../../assets/trash-hover.svg" alt="" />
        </button>
        <button type="button" tabindex="-1" class="drag-handle" title="Drag to reorder" draggable="true" @dragstart="$emit('dragStart', $event)" @dragend="$emit('dragEnd')">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </td>
  </tr>
</template>

<script>
import QuoteProductSearch from './QuoteProductSearch.vue';
import QuoteLineNumberInput from './QuoteLineNumberInput.vue';
import { baseUomForProduct, calculateLineAmount, formatCurrency, formatProductDescription, priceForProduct, productSupportsFt2 } from '@/composables/useQuoteLinePricing';

export default {
  name: 'QuoteLineItemRow',
  components: {
    QuoteProductSearch,
    QuoteLineNumberInput,
  },
  emits: ['lineChanged', 'delete', 'dragStart', 'dragOver', 'drop', 'dragEnd'],
  props: {
    item: {
      type: Object,
      required: true,
    },
    products: {
      type: Array,
      default: () => [],
    },
    pricingLevelId: {
      type: String,
      default: '',
    },
    isBlank: {
      type: Boolean,
      default: false,
    },
    isDragOver: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isComment() {
      return this.item.LINETYPE === 'comment';
    },
    selectedProduct() {
      if (!this.item.PRODUCTID) return null;
      return this.products.find((product) => product.PRODUCTID?.toUpperCase() === this.item.PRODUCTID?.toUpperCase()) || null;
    },
    unitOptions() {
      if (this.item.LINETYPE === 'nonstock') return ['EA', 'BND', 'FT2'];
      const savedUom = ['EA', 'BND', 'FT2'].includes(this.item.UNIT) ? this.item.UNIT : null;
      if (this.item.PRODUCTID && !this.selectedProduct) return savedUom ? [savedUom] : ['EA'];

      const baseUom = baseUomForProduct(this.selectedProduct);
      return productSupportsFt2(this.selectedProduct, this.pricingLevelId) ? [baseUom, 'FT2'] : [baseUom];
    },
    formattedAmount() {
      return formatCurrency(calculateLineAmount(this.item));
    },
    formattedPrice() {
      const price = this.item.UNITPRICE;
      if (price === '' || price === null || price === undefined) return '';
      return formatCurrency(price);
    },
  },
  watch: {
    unitOptions: {
      handler(options) {
        if (this.isComment) return;
        if (!options.includes(this.item.UNIT)) {
          this.item.UNIT = this.item.LINETYPE === 'part' ? baseUomForProduct(this.selectedProduct) : 'EA';
          this.applyCatalogPrice();
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    emitChanged() {
      this.$emit('lineChanged');
    },
    handleTypeChange() {
      if (this.item.LINETYPE === 'comment') {
        this.item.PRODUCTID = null;
        this.item.PRICELISTID = null;
        this.item.QTY = '';
        this.item.UNIT = 'EA';
        this.item.UNITPRICE = '';
        this.item.DISCOUNTPCT = 0;
      } else if (this.item.LINETYPE === 'nonstock') {
        this.item.PRODUCTID = null;
        this.item.PRICELISTID = null;
        this.item.UNIT = 'EA';
      } else {
        this.item.PRODUCTID = null;
        this.item.PRICELISTID = null;
        this.item.DESCRIPTION = '';
        this.item.UNIT = 'EA';
        this.item.UNITPRICE = '';
      }
      this.emitChanged();
    },
    handleProductSearchInput(value) {
      this.item.DESCRIPTION = value;
      if (this.item.PRODUCTID && value !== formatProductDescription(this.selectedProduct)) {
        this.item.PRODUCTID = null;
        this.item.PRICELISTID = null;
      }
      this.emitChanged();
    },
    selectProduct(product) {
      this.item.PRODUCTID = product.PRODUCTID;
      this.item.DESCRIPTION = formatProductDescription(product);
      this.item.UNIT = baseUomForProduct(product);
      this.applyCatalogPrice(product);
      this.emitChanged();
    },
    handleUnitChange() {
      if (this.item.LINETYPE === 'part' && this.item.PRODUCTID) {
        this.applyCatalogPrice();
      }
      this.emitChanged();
    },
    applyCatalogPrice(product = this.selectedProduct) {
      const price = priceForProduct(product, this.pricingLevelId, this.item.UNIT || baseUomForProduct(product));
      this.item.UNITPRICE = price === null || price === undefined ? '' : String(Number(price));
      this.item.PRICELISTID = this.pricingLevelId || null;
    },
  },
};
</script>
